<script setup>
/*
 * Execucoes do n8n em andamento para o contato desta conversa (substitui a secao "Macros").
 * Consulta o backend do Helix, que le o Postgres do n8n e cancela pela API publica.
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LuraCopilotAPI from 'dashboard/api/luraCopilot';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const currentAccountId = useMapGetter('getCurrentAccountId');

const executions = ref([]);
const contact = ref(null);
const error = ref(null);
const loaded = ref(false);
const now = ref(Date.now());
const stopping = ref({});

let liveTimer = null;
let fullTimer = null;
let clockTimer = null;
let inFlight = false;

const LIVE_INTERVAL = 3000;
const FULL_INTERVAL = 20000;

const load = async (scope = 'live') => {
  if (inFlight || !props.conversationId) return;
  inFlight = true;
  try {
    const data = await LuraCopilotAPI.listExecutions(
      currentAccountId.value,
      props.conversationId,
      scope
    );
    contact.value = data.contact;
    if (scope === 'all') {
      executions.value = data.executions;
    } else {
      // Mantem as terminadas ja conhecidas; substitui as ativas
      const finished = executions.value.filter(e => !e.live);
      const liveIds = new Set(data.executions.map(e => e.id));
      executions.value = [
        ...data.executions,
        ...finished.filter(e => !liveIds.has(e.id)),
      ];
    }
    error.value = null;
    loaded.value = true;
  } catch (e) {
    error.value = e.message || 'Falha ao consultar execuções';
  } finally {
    inFlight = false;
  }
};

const stop = () => {
  if (liveTimer) clearInterval(liveTimer);
  if (fullTimer) clearInterval(fullTimer);
  if (clockTimer) clearInterval(clockTimer);
  liveTimer = null;
  fullTimer = null;
  clockTimer = null;
};

const start = () => {
  stop();
  load('all');
  liveTimer = setInterval(() => load('live'), LIVE_INTERVAL);
  fullTimer = setInterval(() => load('all'), FULL_INTERVAL);
  clockTimer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
};

onMounted(start);
onBeforeUnmount(stop);
watch(
  () => props.conversationId,
  () => {
    executions.value = [];
    loaded.value = false;
    start();
  }
);

const live = computed(() => executions.value.filter(e => e.live));
const recent = computed(() => executions.value.filter(e => !e.live));

const shortName = name =>
  String(name || '')
    .replace(/^NATALIA SUPREMA\s*/i, 'Natália ')
    .replace(/\s+/g, ' ')
    .trim();

const elapsedFor = execution => {
  const startMs = new Date(
    execution.startedAt || execution.createdAt
  ).getTime();
  const ms = execution.live
    ? Math.max(0, now.value - startMs)
    : execution.durationMs || 0;
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
};

const statusLabel = status =>
  ({
    new: 'na fila',
    running: 'executando',
    waiting: 'aguardando',
    success: 'concluída',
    error: 'erro',
    crashed: 'falhou',
    canceled: 'cancelada',
  })[status] || status;

const statusClass = status =>
  ({
    new: 'bg-n-slate-3 text-n-slate-11',
    running: 'bg-n-blue-3 text-n-blue-11',
    waiting: 'bg-n-amber-3 text-n-amber-11',
    success: 'bg-n-teal-3 text-n-teal-11',
    error: 'bg-n-ruby-3 text-n-ruby-11',
    crashed: 'bg-n-ruby-3 text-n-ruby-11',
    canceled: 'bg-n-slate-3 text-n-slate-11',
  })[status] || 'bg-n-slate-3 text-n-slate-11';

const startedAtLabel = execution => {
  const d = new Date(execution.startedAt || execution.createdAt);
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const cancel = async execution => {
  if (stopping.value[execution.id]) return;
  stopping.value = { ...stopping.value, [execution.id]: true };
  try {
    await LuraCopilotAPI.stopExecution(
      currentAccountId.value,
      props.conversationId,
      execution.id
    );
    useAlert(`Execução ${execution.id} cancelada.`);
    await load('all');
  } catch (e) {
    useAlert(e.message || 'Não foi possível cancelar a execução.');
  } finally {
    const next = { ...stopping.value };
    delete next[execution.id];
    stopping.value = next;
  }
};
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex flex-col gap-2 text-sm">
    <div v-if="error" class="text-xs text-n-ruby-11">{{ error }}</div>

    <div v-if="!loaded && !error" class="text-xs text-n-slate-10">
      Consultando o n8n…
    </div>

    <template v-else>
      <div
        v-if="!live.length"
        class="flex items-center gap-2 text-xs text-n-slate-10"
      >
        <Icon icon="i-lucide-circle-dashed" class="w-3.5 h-3.5" />
        Nenhuma execução em andamento para este contato
      </div>

      <div
        v-for="execution in live"
        :key="execution.id"
        class="rounded-lg border border-n-blue-8 bg-n-blue-2 p-2 flex flex-col gap-1"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <Icon
              icon="i-lucide-loader-circle"
              class="w-4 h-4 animate-spin text-n-blue-11 flex-shrink-0"
            />
            <span class="text-n-slate-12 truncate font-medium">
              {{ shortName(execution.workflowName) }}
            </span>
          </div>
          <span
            class="px-1.5 h-4 rounded text-[10px] font-medium uppercase tracking-wide flex-shrink-0"
            :class="statusClass(execution.status)"
          >
            {{ statusLabel(execution.status) }}
          </span>
        </div>
        <div
          class="flex items-center justify-between gap-2 text-xs text-n-slate-11"
        >
          <span class="font-mono">
            #{{ execution.id }} · {{ startedAtLabel(execution) }} ·
            <span class="text-n-slate-12 font-semibold">{{
              elapsedFor(execution)
            }}</span>
          </span>
          <Button
            label="Cancelar"
            icon="i-lucide-square"
            ruby
            faded
            xs
            :is-loading="!!stopping[execution.id]"
            @click="cancel(execution)"
          />
        </div>
      </div>

      <template v-if="recent.length">
        <div class="text-[10px] uppercase tracking-wide text-n-slate-10 mt-1">
          Últimas 30 min
        </div>
        <div
          v-for="execution in recent"
          :key="execution.id"
          class="flex items-center justify-between gap-2 text-xs text-n-slate-11 py-0.5"
        >
          <span class="truncate">
            <span class="font-mono text-n-slate-10">#{{ execution.id }}</span>
            {{ shortName(execution.workflowName) }}
          </span>
          <span class="flex items-center gap-1.5 flex-shrink-0">
            <span class="text-n-slate-10">{{ elapsedFor(execution) }}</span>
            <span
              class="px-1.5 h-4 rounded text-[10px] font-medium uppercase tracking-wide"
              :class="statusClass(execution.status)"
            >
              {{ statusLabel(execution.status) }}
            </span>
          </span>
        </div>
      </template>
    </template>
  </div>
</template>
